import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Modal, ModalActions, ModalContent, ModalTitleInverse, useSnackbar } from '@c2fo/liquidity';
import { OfferConfigDetails } from '@/enterprise/components/OfferConfigDetails';
import useDeleteOfferSchedule from '@/enterprise/data/useDeleteOfferSchedule';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import { buildCustomScheduleSentenceFromExistingOfferSchedule } from '../../offerModal/utils';

interface DeleteRuleModalProps {
  onClose: () => void;
  open: boolean;
  takerMarket: TakerMarket;
}

const DeleteScheduleModal = ({ open, onClose, takerMarket }: DeleteRuleModalProps) => {
  const { trackEnterpriseEvent } = useReporting();
  const { t } = useTranslation();
  const showSnackbar = useSnackbar();
  const [showError, setShowError] = useState(false);
  const [contactSRM, setContactSRM] = useState(false);
  const { mutateAsync: deleteOfferSchedule, isPending } = useDeleteOfferSchedule();

  const offerSchedule = takerMarket.offerSchedule;

  if (!takerMarket || !offerSchedule) {
    return null;
  }

  const handleClose = () => {
    setContactSRM(false);
    setShowError(false);
    onClose();
  };

  const onClickDeleteSchedule = async () => {
    setShowError(false);

    try {
      await deleteOfferSchedule({
        offerUuid: takerMarket.offerConfig.uuid,
      });

      handleClose();
      showSnackbar({ message: t('automation.deleteScheduleSuccess') });
      trackEnterpriseEvent('offer::schedule::deleted');
    } catch {
      setShowError(true);
    }
  };

  return (
    <Modal disableOutsideClickClose={isPending} onClose={handleClose} open={open} size="sm">
      <ModalTitleInverse>{t('automation.deleteSchedule')}</ModalTitleInverse>
      <ModalContent className="space-y-4">
        {showError && (
          <>
            {contactSRM ? (
              <Alert
                description={t('core.contactSrm.description')}
                full
                title={t('core.contactSrm.title')}
                type="info"
              />
            ) : (
              <Alert
                action={{
                  onClick: () => {
                    // TODO - SUPP-16050: Add track event
                    setContactSRM(true);
                  },
                  text: t('core.contactSrm'),
                }}
                description={t('core.reachOutForAssistance')}
                full
                title={t('automation.deleteScheduleError')}
                type="error"
              />
            )}
          </>
        )}
        <div>{t('automation.deleteScheduleWarning')}</div>
        <div className="rounded-md bg-gray-100 p-4 text-sm font-medium">
          {buildCustomScheduleSentenceFromExistingOfferSchedule({
            offerSchedule,
            t,
          })}
        </div>
        <p className="font-medium">{t('automation.deleteScheduleImpact')}</p>
        <OfferConfigDetails
          items={[
            {
              label: t('core.participation'),
              value: takerMarket.offerConfig.isEnabled
                ? t('automation.offerRemainsOn')
                : t('automation.offerRemainsOff'),
            },
            ...(takerMarket.offerConfig.isEnabled === true
              ? [
                  {
                    label: t('automation.futureInvoices'),
                    value: t('automation.futureInvoicesDescription'),
                  },
                ]
              : []),
          ]}
        />
        <div className="text-sm italic">{t('automation.modifyOfferScheduleNote')}</div>
      </ModalContent>
      <ModalActions>
        <Button onClick={handleClose} variant="secondary">
          {t('core.cancel')}
        </Button>
        <Button loading={isPending} onClick={onClickDeleteSchedule} variant="destructive">
          {t('automation.deleteSchedule')}
        </Button>
      </ModalActions>
    </Modal>
  );
};

export default DeleteScheduleModal;
